import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, PartyPopper, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface DragItem {
  id: string;
  text: string;
  image_url?: string;
}

interface DropZone {
  id: string;
  label: string;
  correctItemId: string;
}

interface DragDropProps {
  question: string;
  items: DragItem[];
  zones: DropZone[];
  onAnswer: (placements: Record<string, string>, isCorrect: boolean) => void;
  disabled?: boolean;
}

const DragDropActivity = ({ question, items, zones, onAnswer, disabled }: DragDropProps) => {
  const [placements, setPlacements] = useState<Record<string, string>>({});
  const [availableItems, setAvailableItems] = useState<DragItem[]>([...items]);
  const [answered, setAnswered] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const handleDragStart = (itemId: string) => {
    -(disabled || answered) && setDraggedItem(itemId);
  };

  const handleDrop = useCallback((zoneId: string) => {
    if (!draggedItem || answered || disabled) return;
    
    // If zone already has an item, return it to available
    const existingItemId = placements[zoneId];
    if (existingItemId) {
      const existingItem = items.find(i => i.id === existingItemId);
      if (existingItem) {
        setAvailableItems(prev => [...prev, existingItem]);
      }
    }

    setPlacements(prev => ({ ...prev, [zoneId]: draggedItem }));
    setAvailableItems(prev => prev.filter(i => i.id !== draggedItem));
    setDraggedItem(null);
  }, [draggedItem, placements, items, answered, disabled]);

  const handleRemoveFromZone = (zoneId: string) => {
    if (answered || disabled) return;
    const itemId = placements[zoneId];
    if (!itemId) return;
    const item = items.find(i => i.id === itemId);
    if (item) setAvailableItems(prev => [...prev, item]);
    setPlacements(prev => {
      const next = { ...prev };
      delete next[zoneId];
      return next;
    });
  };

  const handleSubmit = () => {
    const allCorrect = zones.every(z => placements[z.id] === z.correctItemId);
    setAnswered(true);
    onAnswer(placements, allCorrect);
  };

  const allPlaced = zones.every(z => placements[z.id]);

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-foreground text-center">{question}</h3>

      {/* Available items */}
      <div className="flex flex-wrap gap-2 justify-center min-h-[60px] p-3 rounded-xl bg-muted/30 border-2 border-dashed border-muted-foreground/20">
        {availableItems.length === 0 && !answered && (
          <span className="text-sm text-muted-foreground">تم وضع جميع العناصر ✓</span>
        )}
        {availableItems.map(item => (
          <motion.div
            key={item.id}
            layout
            draggable={!answered && !disabled}
            onDragStart={() => handleDragStart(item.id)}
            onDragEnd={() => setDraggedItem(null)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg border-2 bg-background shadow-sm cursor-grab active:cursor-grabbing",
              draggedItem === item.id && "opacity-50 border-primary"
            )}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            {item.image_url && <img src={item.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
            <span className="font-medium text-sm">{item.text}</span>
          </motion.div>
        ))}
      </div>

      {/* Drop zones */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {zones.map(zone => {
          const placedItemId = placements[zone.id];
          const placedItem = placedItemId ? items.find(i => i.id === placedItemId) : null;
          const isCorrect = answered && placedItemId === zone.correctItemId;
          const isWrong = answered && placedItemId && placedItemId !== zone.correctItemId;

          return (
            <div
              key={zone.id}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-primary"); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove("ring-2", "ring-primary"); }}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("ring-2", "ring-primary"); handleDrop(zone.id); }}
              className={cn(
                "p-4 rounded-xl border-2 border-dashed min-h-[80px] transition-all flex flex-col items-center justify-center gap-2",
                !placedItem && "border-muted-foreground/30 bg-muted/10",
                placedItem && !answered && "border-primary/50 bg-primary/5",
                isCorrect && "border-green-500 bg-green-50 dark:bg-green-950/30",
                isWrong && "border-red-500 bg-red-50 dark:bg-red-950/30"
              )}
            >
              <span className="text-xs font-semibold text-muted-foreground">{zone.label}</span>
              {placedItem ? (
                <div className="flex items-center gap-2">
                  {placedItem.image_url && <img src={placedItem.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                  <span className="font-medium text-sm">{placedItem.text}</span>
                  {!answered && (
                    <button onClick={() => handleRemoveFromZone(zone.id)} className="text-muted-foreground hover:text-destructive">
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                  {isCorrect && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                  {isWrong && <XCircle className="w-5 h-5 text-red-500" />}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground/60">اسحب العنصر هنا</span>
              )}
            </div>
          );
        })}
      </div>

      {!answered && (
        <div className="flex justify-center">
          <Button onClick={handleSubmit} disabled={!allPlaced} className="px-8 gap-2">
            تأكيد الإجابة
          </Button>
        </div>
      )}

      <AnimatePresence>
        {answered && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "text-center p-4 rounded-xl",
              zones.every(z => placements[z.id] === z.correctItemId) ? "bg-green-50 dark:bg-green-950/30" : "bg-amber-50 dark:bg-amber-950/30"
            )}
          >
            {zones.every(z => placements[z.id] === z.correctItemId) ? (
              <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
                <PartyPopper className="w-5 h-5" />
                <span className="font-bold">ممتاز! جميع الإجابات صحيحة 🌟</span>
              </div>
            ) : (
              <span className="font-bold text-amber-700 dark:text-amber-400">
                بعض الإجابات خاطئة. حاول مرة أخرى! 💪
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DragDropActivity;
